import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Location } from './Location'
import { ReportTotal } from './ReportTotal'
import { User } from './User'

type ReportResultConstructorArgs = Omit<ConstructorArgs<() => ReportResult>, 'image_type'> & {
    image_type: string | null
}

export enum ReportImageType {
    ReportImageTypeUnknown,
    ReportImageTypeImage,
    ReportImageTypeIcon,
    ReportImageTypeInitials,
}

export class ReportResult extends BaseRecord {
    readonly recordType = 'reportresult'

    readonly type?: string
    readonly subtype?: string
    readonly title?: string
    readonly subtitle?: string
    readonly imageUri?: string
    readonly total?: ReportTotal
    private readonly _imageType?: string

    constructor(data?: Partial<ReportResultConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.type = data.type ?? undefined
        this.subtype = data.subtype ?? undefined
        this.title = data.title ?? undefined
        this.subtitle = data.subtitle ?? undefined
        this.imageUri = data.image_uri ?? undefined
        this.total = data.total
            ? data.total instanceof ReportTotal
                ? data.total
                : new ReportTotal(this.propertyFromDictionaryArg(data.total)?.serialize())
            : undefined
        this._imageType = data.image_type ?? undefined
    }

    clone(overrides?: Partial<ReportResultConstructorArgs>): ReportResult {
        const imageTypeString = (() => {
            switch (this.imageType) {
                case ReportImageType.ReportImageTypeIcon: {
                    return 'icon'
                }
                case ReportImageType.ReportImageTypeImage: {
                    return 'image'
                }
                case ReportImageType.ReportImageTypeInitials: {
                    return 'initials'
                }
                default:
                    return undefined
            }
        })()

        return new ReportResult({
            ...this.serializeForClone(),
            image_type: imageTypeString,
            ...overrides,
        })
    }

    get imageType(): ReportImageType {
        if (this._imageType === 'icon') return ReportImageType.ReportImageTypeIcon
        if (this._imageType === 'image') return ReportImageType.ReportImageTypeImage
        if (this._imageType === 'initials') return ReportImageType.ReportImageTypeInitials
        return ReportImageType.ReportImageTypeUnknown
    }

    // no fk because these are not stored in sql lite

    get person(): User | undefined {
        return this.getForeignRecord('person', User)
    }
    set person(person: User | undefined) {
        this.setForeignRecord('person', person)
    }
    get location(): Location | undefined {
        return this.getForeignRecord('location', Location)
    }
    set location(location: Location | undefined) {
        this.setForeignRecord('location', location)
    }
}
